/**
 * KeyboardHelpModal
 *
 * Reusable shortcut cheat-sheet dialog (Linear / GitHub / Notion pattern).
 * Render at the page level; open via `?` key or a visible help button.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - title?: string (defaults to "Keyboard shortcuts")
 *   - groups: Array<{ heading, items: Array<{ keys, label }> }>
 */
import React, { useEffect, useRef, useCallback } from "react";
import "./KeyboardHelpModal.css";

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const KeyboardHelpModal = ({
  open,
  onClose,
  title = "Keyboard shortcuts",
  groups = [],
}) => {
  const dialogRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const titleId = "kb-help-title";

  const handleKeyDown = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = Array.from(root.querySelectorAll(FOCUSABLE)).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [open, onClose]
  );

  useEffect(() => {
    if (!open) return undefined;
    lastFocusedRef.current = document.activeElement;
    const root = dialogRef.current;
    if (root) {
      const first = root.querySelector(FOCUSABLE);
      first?.focus();
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      const last = lastFocusedRef.current;
      if (last && typeof last.focus === "function") last.focus();
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="kb-help-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className="kb-help-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="kb-help-header">
          <h2 id={titleId} className="kb-help-title">
            {title}
          </h2>
          <button
            type="button"
            className="kb-help-close"
            aria-label="Close keyboard shortcuts"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="kb-help-body">
          {groups.map((group) => (
            <section key={group.heading} className="kb-help-group">
              <h3 className="kb-help-group-heading">{group.heading}</h3>
              <ul className="kb-help-list">
                {group.items.map((item, idx) => (
                  <li key={idx} className="kb-help-item">
                    <span className="kb-help-keys">
                      {item.keys.map((k, i) => (
                        <kbd key={i} className="kb-help-key">
                          {k}
                        </kbd>
                      ))}
                    </span>
                    <span className="kb-help-label">{item.label}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KeyboardHelpModal;
